import { useSEO, breadcrumb } from "../lib/seo";
import { SITE, facebookLink, waLink, telegramLink } from "../lib/site";
import { trackCta, trackEvent } from "../lib/analytics";
import PageHero from "../components/PageHero";
import { Facebook, MessageCircle, Send, ArrowRight, Info } from "lucide-react";

export default function Contact() {
  useSEO({
    title: "Kontak Sloto69 | Pusat Bantuan Pengguna",
    description: "Hubungi tim bantuan Sloto69 melalui WhatsApp, Telegram, atau Facebook untuk mendapatkan informasi resmi yang Anda butuhkan.",
    keywords: "kontak sloto69, bantuan sloto69, hubungi sloto69",
    canonical: `${SITE.baseUrl}/kontak`,
    jsonLd: breadcrumb([
      { name: "Beranda", url: `${SITE.baseUrl}/` },
      { name: "Kontak", url: `${SITE.baseUrl}/kontak` },
    ]),
  });

  const submit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const nama = fd.get("nama") || "";
    const kontak = fd.get("kontak") || "";
    const pesan = fd.get("pesan") || "";
    const text = encodeURIComponent(`Halo Sloto69, saya ${nama} (${kontak}). ${pesan}`);
    trackEvent("submit_contact", {
      event_category: "form",
      event_label: "Contact Page Form",
      location: "contact_page",
    });
    window.open(`https://api.whatsapp.com/send?phone=${SITE.whatsappNumber}&text=${text}`, "_blank", "noopener");
  };

  return (
    <>
      <PageHero
        eyebrow="Kontak"
        title="Hubungi Tim Bantuan Sloto69"
        description="Tim bantuan siap membantu pengguna mendapatkan informasi yang dibutuhkan. Pilih kanal yang paling nyaman bagi Anda."
        breadcrumb={[{ label: "Kontak" }]}
      />

      <section className="section-pad" data-testid="contact-page">
        <div className="shell grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5 grid gap-4 self-start">
            <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="contact-page-whatsapp" onClick={() => trackCta("click_whatsapp", "WhatsApp", "contact_page")} className="card-premium p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-[#10B981]/15 flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-[#10B981]" />
              </div>
              <div>
                <p className="font-semibold">WhatsApp</p>
                <p className="text-xs text-slate-400">Hubungi tim bantuan via WA</p>
              </div>
            </a>
            <a href={telegramLink} target="_blank" rel="noopener noreferrer" data-testid="contact-page-telegram" onClick={() => trackCta("click_telegram", "Telegram", "contact_page")} className="card-premium p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-[#D4AF37]/15 flex items-center justify-center">
                <Send className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <p className="font-semibold">Telegram</p>
                <p className="text-xs text-slate-400">Akses kanal Telegram resmi</p>
              </div>
            </a>
            <a href={facebookLink} target="_blank" rel="noopener noreferrer" data-testid="contact-page-facebook" onClick={() => trackCta("click_facebook", "Facebook", "contact_page")} className="card-premium p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-[#1877F2]/15 flex items-center justify-center">
                <Facebook className="w-5 h-5 text-[#60A5FA]" />
              </div>
              <div>
                <p className="font-semibold">Facebook</p>
                <p className="text-xs text-slate-400">Gabung grup Facebook resmi</p>
              </div>
            </a>
            <div className="card-premium p-5 text-xs text-slate-400 leading-relaxed flex gap-3">
              <Info className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#D4AF37]" />
              <span>
                Kami akan merespons pertanyaan Anda secepat mungkin di jam kerja. Hindari membagikan
                data sensitif pada pesan pertama; kami akan menyampaikan instruksi yang dibutuhkan.
              </span>
            </div>
          </div>

          <form
            onSubmit={submit}
            data-testid="contact-page-form"
            className="lg:col-span-7 card-premium p-6 lg:p-8 grid gap-4 self-start"
          >
            <h2 className="font-display text-2xl sm:text-3xl">Form Bantuan</h2>
            <p className="text-sm text-slate-400">
              Pesan Anda akan diteruskan ke kanal WhatsApp resmi kami untuk respons lebih cepat.
            </p>
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              <label className="grid gap-2">
                <span className="text-xs uppercase tracking-wider text-slate-400">Nama</span>
                <input
                  required
                  type="text"
                  name="nama"
                  data-testid="contact-page-input-nama"
                  placeholder="Nama lengkap Anda"
                  className="bg-[#0F172A]/70 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#10B981]/60 focus:ring-1 focus:ring-[#10B981]/30"
                />
              </label>
              <label className="grid gap-2">
                <span className="text-xs uppercase tracking-wider text-slate-400">Kontak</span>
                <input
                  required
                  type="text"
                  name="kontak"
                  data-testid="contact-page-input-kontak"
                  placeholder="Nomor WhatsApp Anda"
                  className="bg-[#0F172A]/70 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#10B981]/60 focus:ring-1 focus:ring-[#10B981]/30"
                />
              </label>
            </div>
            <label className="grid gap-2">
              <span className="text-xs uppercase tracking-wider text-slate-400">Pesan</span>
              <textarea
                required
                name="pesan"
                data-testid="contact-page-input-pesan"
                rows={6}
                placeholder="Tulis pesan Anda di sini..."
                className="bg-[#0F172A]/70 border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#10B981]/60 focus:ring-1 focus:ring-[#10B981]/30"
              />
            </label>
            <button type="submit" data-testid="contact-page-submit" className="btn-primary justify-self-start mt-2">
              Kirim Pesan <ArrowRight className="w-4 h-4" />
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
